import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Tooltip, Space } from 'antd';
import moment from 'moment';
import { formatBytes } from 'utils/format/file';
import StackDetail from 'components/exception/issue/StackDetail';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import { ze } from 'utils/zhEn';
import lastReportScss from 'components/exception/issue/LastReport/style.scss';

/*
* crashLogData的数据样例：
 * {
 *   "fileInfo": {
 *     "actualSize": 64868,
 *     "magicNumber": "0xFFEECCBB",
 *     "version": 2,
 *     "createTime": "Wed Aug 27 15:29:35 GMT+08:00 2025",
 *     "updateTime": "Wed Aug 27 15:29:35 GMT+08:00 2025",
 *     "additionalDataSize": 64036,
 *     "loggerItemWriteIndex": 3,
 *     "capacity": 10,
 *     "elementSize": 264
 *   },
 *   "images": [
 *     {
 *       "name": "libLogRedirect.dylib",
 *       "uuid": "9b64076c9844384fa3a0a827b6dcca57",
 *       "arch": "arm64",
 *       "loadAddress": "0x100950000",
 *       "endAddress": "0x0000000100953fff"
 *     },
 *     {
 *       "name": "CrashSightSDKDemo",
 *       "uuid": "7acb51c3084a3a7abc3d7326b502d876",
 *       "arch": "arm64",
 *       "loadAddress": "0x100900000",
 *       "endAddress": "0x000000010090bfff"
 *     },
 *     {
 *       "name": "CrashSight",
 *       "uuid": "62aeb334ea9e316883073d3956489a0b",
 *       "arch": "arm64",
 *       "loadAddress": "0x100d7c000",
 *       "endAddress": "0x0000000100ef3fff"
 *     }
 *   ],
 *   "mallocLogs": [
 *     {
 *       "writeIndex": 0,
 *       "isLatest": false,
 *       "timeDiff": 825,  // timeDiff单位是毫秒（雍彭亮确认过的）
 *       "mallocSize": 104857600,
 *       "stackTrace": [
 *         {
 *           "index": 0,
 *           "library": "libsystem_malloc.dylib",
 *           "address": "0x00000001949ba528",
 *           "baseAddress": "0x000000019498d000",
 *           "offset": 185640,
 *           "fullSymbol": "0  libsystem_malloc.dylib 0x00000001949ba528 0x000000019498d000 + 185640\r"
 *         },
 *         {
 *           "index": 1,
 *           "library": "CrashSightSDKDemo",
 *           "address": "0x00000001009053f4",
 *           "baseAddress": "0x0000000100900000",
 *           "offset": 21492,
 *           "fullSymbol": "1  CrashSightSDKDemo 0x00000001009053f4 0x0000000100900000 + 21492\r"
 *         },
 *         {
 *           "index": 2,
 *           "library": "UIKitCore",
 *           "address": "0x000000018714508c",
 *           "fullSymbol": "2  UIKitCore 0x000000018714508c -[UIApplication sendAction:to:from:forEvent:] +  100\r"
 *         }
 *       ]
 *     }
 *   ],
 *   "referenceTime": 1756279789200,
 *   "summary": {
 *     "totalModules": 741,
 *     "totalMallocLogs": 3,
 *     "maxReadableLogs": 10,
 *     "criticalAllocation": {
 *       "allocCount": 3
 *     }
 *   }
 * }
 *
 */


/**
 * MallocLogger 组件 - 内存分配日志展示组件
 *
 * 功能说明：
 * 1. 展示崩溃日志中的内存分配(malloc)记录
 * 2. 显示每次内存分配的绝对时间、分配大小、调用栈信息
 * 3. 支持表格排序、展开查看详细调用栈
 *
 * 时间计算逻辑：
 * - referenceTime: 基准时间戳（毫秒）
 * - timeDiff: 每条记录相对于referenceTime的时间差（毫秒）
 * - 绝对时间 = referenceTime + 当前记录的timeDiff
 * - 显示格式: YYYY-MM-DD HH:mm:ss.SSS
 * - 特殊情况：若referenceTime为falsy（nullish或0，旧版SDK未采集到），
 *   则绝对时间没有意义，此时只展示timeDiff对应的相对时间，格式为 HH:mm:ss.SSS
 *
 * Props:
 * @param {string} pid - 进程ID
 * @param {object} issue - 问题对象信息
 * @param {object} crashLogData - 崩溃日志数据，包含mallocLogs、referenceTime等，可能为null
 *
 */
const MallocLogger = ({
  pid,
  issue,
  crashLogData,
}) => {
  const appSoNameList = useMemo(() => {
    const { crashDoc } = issue ? issue.toJS() : {};
    const esMap = crashDoc?.esMap || {};
    const {
      appSoNameList,
    } = esMap;
    return appSoNameList;
  }, [issue]);

  // referenceTime缺失（nullish或0，旧版SDK未采集到）时，Time列只能展示相对时间，需要提示用户
  const hasReferenceTime = !!crashLogData?.referenceTime;
  // AllocLogger 1003 起附件才携带 alloc_type；1002 及更早数据不展示 Type 列，避免误显示 heap
  const showAllocTypeColumn = (crashLogData?.fileInfo?.version ?? 0) >= 1003;

  /**
   * 处理表格数据
   * 使用useMemo优化性能，预计算表格列需要的值
   */
  const tableData = useMemo(() => {
    // 安全获取mallocLogs数据，避免空值异常
    let mallocLogs = crashLogData?.mallocLogs || [];

    if (!mallocLogs.length) {
      return [];
    }

    // 按照writeIndex排序（从小到大）
    mallocLogs = [...mallocLogs].sort((a, b) => (a.writeIndex || 0) - (b.writeIndex || 0));

    return mallocLogs.map((log, index) => {
      // 计算时间戳。referenceTime为falsy时（旧版SDK未采集到），无法计算有意义的绝对时间，只能展示timeDiff对应的相对时间
      const timeDiff = mallocLogs[index]?.timeDiff || 0;
      const referenceTime = crashLogData?.referenceTime;
      const absoluteTimestamp = hasReferenceTime ? referenceTime + timeDiff : undefined;

      return {
        key: index, // React表格组件所需的唯一key
        originalIndex: index + 1, // 显示用的序号，从1开始（用户友好）
        // 预计算的列值
        formattedTime: hasReferenceTime
          ? moment(absoluteTimestamp).format('YYYY-MM-DD HH:mm:ss.SSS') // 有referenceTime时展示绝对时间
          : moment.utc(timeDiff).format('HH:mm:ss.SSS'), // 无referenceTime时只展示相对时间
        formattedSize: log.mallocSize ? formatBytes(log.mallocSize) : '-', // 格式化大小
        responsibleCaller: (() => {
          // 遍历stackTrace，查找第一个library在appSoNameList中的item
          const stackTrace = log?.stackTrace || [];
          const appSoNames = appSoNameList || [];

          for (const stackItem of stackTrace) {
            if (stackItem?.library) {
              // 大小写不敏感比较
              const foundInAppSo = appSoNames.some(appSoName =>
                appSoName && stackItem.library.toLowerCase() === appSoName.toLowerCase()
              );
              if (foundInAppSo) {
                return stackItem.fullSymbol || '-';
              }
            }
          }

          // 如果找不到满足条件的item，返回横线
          return '-';
        })(), // 立即执行函数
        absoluteTimestamp, // 保留绝对时间戳（可能为undefined），供有referenceTime时排序使用
        ...log, // 展开原始的malloc日志数据
      };
    });
  }, [crashLogData, appSoNameList]);

  // 如果没有malloc日志数据，直接返回null不渲染组件
  if (!tableData.length) {
    return null;
  }

  // 表格列定义
  const columns = [
    {
      title: '#',
      dataIndex: 'originalIndex',
      key: 'originalIndex',
      width: '50px',
      sorter: (a, b) => a.originalIndex - b.originalIndex,
    },
    {
      // Time列：referenceTime存在时显示绝对时间，否则显示相对时间，并提示用户
      title: hasReferenceTime
        ? 'Time'
        : <Tooltip
          placement='top'
          title={ze(
            '缺少基准时间，此处展示的是相对时间（与监听起点的耗时差），仅供比较记录间的先后与间隔，不代表实际发生时刻',
            'Reference time is missing. The value shown is a relative time (elapsed since monitoring started), useful only for comparing order/interval between records, not the actual occurrence time.'
          )}
        ><Space>
          <div>Time</div>
          <WrappedTipsIcon />
        </Space></Tooltip>,
      dataIndex: 'formattedTime',
      key: 'time',
      width: '25%',
      // 排序器：由于referenceTime在所有记录间是同一个常量（或均缺失），
      // 直接用timeDiff比较即可得到与时间先后一致的排序结果
      sorter: (a, b) => (a.timeDiff || 0) - (b.timeDiff || 0),
    },
    {
      title: 'Size',
      dataIndex: 'formattedSize',
      key: 'size',
      width: appSoNameList?.length >= 0 ? '15%' : undefined,
      sorter: (a, b) => {
        // 基于原始数值进行排序，而不是格式化后的字符串
        return (a.mallocSize || 0) - (b.mallocSize || 0);
      },
    },
    showAllocTypeColumn && {
      title: 'Type',
      dataIndex: 'allocType',
      key: 'allocType',
      width: '15%',
      render: (allocType) => allocType || '-',
      sorter: (a, b) => String(a.allocType || '').localeCompare(String(b.allocType || '')),
    },
    appSoNameList?.length >= 0 && {
      title: 'Responsible Caller',
      dataIndex: 'responsibleCaller',
      key: 'responsibleCaller',
      sorter: (a, b) => {
        return a.responsibleCaller.localeCompare(b.responsibleCaller);
      },
    },
  ].filter(x => x);

  /**
   * 展开行渲染函数
   * 当用户点击表格行的展开按钮时，显示该记录的详细调用栈信息
   *
   * @param {object} record - 当前行的数据记录
   * @returns {JSX.Element} 渲染的调用栈详情组件
   */
  const expandedRowRender = (record) => {
    const stackTrace = record?.stackTrace || [];

    // 将调用栈数组转换为字符串格式，过滤空值
    const stackContent = stackTrace
      .map(stack => stack?.fullSymbol || '')
      .filter(symbol => symbol.trim() !== '')
      .join('\n');

    // 如果没有调用栈信息，显示提示文本
    if (!stackContent) {
      return <div>No stack trace available</div>;
    }

    // 使用StackDetail组件展示调用栈，支持符号化等功能
    return (<div className={lastReportScss.data_container}><div className={lastReportScss.data_detail}>
      <StackDetail
        stackContent={stackContent}
        issue={issue}
        pid={pid}
        isNeedHighLight={false} // 不需要高亮显示
        forceParseStack={true}
      />
    </div></div>);
  };

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      pagination={false}
      expandable={{
        expandedRowRender,
        rowExpandable: (record) => {
          const stackTrace = record?.stackTrace || [];
          return stackTrace.length > 0;
        },
      }}
      size="small"
    />
  );
};

MallocLogger.propTypes = {
  pid: PropTypes.string,
  issue: PropTypes.object,
  crashLogData: PropTypes.object,
};

export default MallocLogger;
